import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

// ✅ Evita quebrar por alias "@/"
// Ajuste o caminho conforme sua estrutura:
import { Button } from '../ui/button'; // <- MUDE AQUI se necessário

function normalizePhoneToWa(phone) {
  const raw = String(phone || '').trim();
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  const wa = digits.startsWith('55') ? digits : `55${digits}`;
  if (wa.length < 12) return null;
  return wa;
}

const Header = ({ siteConfig, slug }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isCatalog = useMemo(() => pathname.includes('/imoveis'), [pathname]);
  const homePath = `/site/${slug}`;
  const catalogPath = `/site/${slug}/imoveis`;

  const nome =
    siteConfig?.nome ||
    siteConfig?.displayName ||
    siteConfig?.name ||
    'Corretor';

  const waNumber = normalizePhoneToWa(siteConfig?.whatsapp);
  const waHref = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent(
        `Olá ${nome}! Vim pelo seu site e gostaria de mais informações.`
      )}`
    : null;

  const navLinks = [
    { href: '#sobre', label: 'Sobre', type: 'anchor' },
    { href: catalogPath, label: 'Imóveis', type: 'route' },
    { href: '#servicos', label: 'Serviços', type: 'anchor' },
    { href: '#depoimentos', label: 'Depoimentos', type: 'anchor' },
    { href: '#contato', label: 'Contato', type: 'anchor' },
  ];

  // ✅ Navega + rola pro anchor (funciona mesmo vindo do catálogo)
  const goToAnchor = useCallback(
    async (hash) => {
      setIsMobileMenuOpen(false);

      if (!hash?.startsWith('#')) return;

      if (isCatalog) {
        navigate(homePath, { replace: false });
        // espera a DOM montar antes de tentar rolar
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = document.querySelector(hash);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        });
      } else {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.location.hash = hash; // fallback
      }
    },
    [isCatalog, navigate, homePath]
  );

  // ✅ “Gold” sem depender de variant custom do shadcn
  const goldBtnClass =
    'gap-2 bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <nav className="flex items-center justify-between h-20">
          <Link to={homePath} className="flex items-center gap-2">
            <span className="font-serif text-2xl font-semibold text-foreground tracking-wide">
              {nome}
            </span>
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) =>
              link.type === 'route' ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => goToAnchor(link.href)}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  {link.label}
                </button>
              )
            )}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {waHref ? (
              <a href={waHref} target="_blank" rel="noreferrer">
                <Button size="lg" className={goldBtnClass}>
                  <Phone className="w-4 h-4" />
                  Falar no WhatsApp
                </Button>
              </a>
            ) : (
              <Button size="lg" className={goldBtnClass} disabled>
                <Phone className="w-4 h-4" />
                WhatsApp
              </Button>
            )}
          </div>

          {/* Mobile button */}
          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-20 left-0 right-0 bg-background border-b border-border">
            <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navLinks.map((link) =>
                link.type === 'route' ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-base font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.label}
                    type="button"
                    onClick={() => goToAnchor(link.href)}
                    className="text-left text-base font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                  >
                    {link.label}
                  </button>
                )
              )}

              {waHref ? (
                <a href={waHref} target="_blank" rel="noreferrer">
                  <Button size="lg" className={`mt-4 w-full ${goldBtnClass}`}>
                    <Phone className="w-4 h-4" />
                    Falar no WhatsApp
                  </Button>
                </a>
              ) : (
                <Button
                  size="lg"
                  className={`mt-4 w-full ${goldBtnClass}`}
                  disabled
                >
                  <Phone className="w-4 h-4" />
                  WhatsApp
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
